import { AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { take } from 'rxjs';
import { Project } from 'src/app/shared/models/project';
import { EEAOutputProjectStep1n2ResultSummaryColdef } from './eea-output-project-step1n2-result-summary.coldef';
import { EEAOutputProjectSummaryResultSurfaceWaterApiService } from '../../../../../shared/services/out-put-project/eea-output-project-step1n2-summary-result.api.service';
import { GridOptions, RowSelectedEvent } from 'ag-grid-community';
import { GridComponent } from 'src/app/shared/components/grid/grid.component';
import { EEAOutputProjectLogicService } from '../../eea-output-project.logic.service';
@Component({
  selector: 'app-eea-output-project-step1n2-result-summary',
  templateUrl: './eea-output-project-step1n2-result-summary.component.html',
  styleUrls: ['./eea-output-project-step1n2-result-summary.component.css']
})
export class EEAOutputProjectStep1n2ResultSummaryComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() selectedProject?: Project;
  @Input() selectedModel?:string;
  @Input({required: true}) isProjectOwnershipValid: boolean = false;

  @ViewChild('grid') grid!: GridComponent;

  rowData: any;
  columDef: any;
  loading: boolean = false;

  pivotEnabled: boolean = true;
  private pivotListener: (() => void) | null = null;
  private readonly cdr = inject(ChangeDetectorRef);
  private initialColumnState: any[] | null = null;
  private lastCapturedGridApi: any = null;
  private readonly pivotDynamicColumns = ['SevenDaysPecSwTwa', 'PecSwTwa21Days'];

  sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: false,
          suppressValues: false,
          suppressPivots: false,
          suppressPivotMode: false,
          suppressColumnSelectAll: false,
          suppressColumnExpandAll: false,
          suppressFilters: false
        }
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
        toolPanelParams: {
          suppressFilterSearch: false,
          suppressSelectAll: false
        }
      }
    ],
    defaultToolPanel: 'columns'
  };

  autoGroupColumnDef = {
    cellRendererParams: {
      suppressCount: true
    }
  };

  gridOptions: GridOptions = {
    processPivotResultColDef: (colDef: any) => {
      let typeIndicator = '';
      switch (colDef.field) {
        case 'MaxPecSw':         colDef.headerName = 'Max PECsw (µg/L)'; break;
        case 'SevenDaysPecSwTwa': colDef.headerName = '7d - PECsw,twa (µg/L)'; break;
        case 'PecSwTwa21Days':    colDef.headerName = '21d - PECsw,twa (µg/L)'; break;
      }
      colDef.headerComponent = 'compoundHeader';
    }
  };

  private eeaOutputProjectlogicService = inject(EEAOutputProjectLogicService);

  constructor(private step1n2ResultSummaryColdef: EEAOutputProjectStep1n2ResultSummaryColdef,
              private summaryResultService: EEAOutputProjectSummaryResultSurfaceWaterApiService) {
  }

  ngOnInit(): void {
    this.columDef = this.step1n2ResultSummaryColdef.getColumnsDefinition( this.isProjectOwnershipValid );
    if(this.selectedProject)
      this.getSummaryResult(this.selectedProject.projectPk);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedProject'].currentValue.projectPk) {
      this.getSummaryResult(changes['selectedProject'].currentValue.projectPk);
    }
  }

  getSummaryResult(projectPk: number) {
    if (projectPk != 0) {
      this.loading = true;
      this.summaryResultService
        .getSummariesResultsSurfaceWaterByModel(projectPk,this.selectedModel??"")
        .pipe(take(1))
        .subscribe((result: any) => {
           this.rowData = result.Value;
           this.refreshSelectedValuesTimeout();
           this.loading = false;
        });
    }
  }

  public onSelectedRowEvent( event: RowSelectedEvent ){
    this.eeaOutputProjectlogicService.setSmeSelectionResultsSw( event );
  }

  public onSelectedAllChanged( ){
    if( !this.grid ) return;
    this.eeaOutputProjectlogicService.setSmeSelectionsResultsSw( this.grid );
  }

  public onSignOffClicked( ){
    if( !this.grid ) return;
    this.eeaOutputProjectlogicService.signOffSmeSelectionsResultsSw( this.grid );
  }

  private refreshSelectedValuesTimeout(): void {
    setTimeout(() => {
      this.refreshSelectedValues();
    }, 75);
  }

  private refreshSelectedValues(): void {
    if( !this.grid ) return;
    this.grid.refreshSelectedValues( 'EeaSmeSelection' );
  }

  ngAfterViewInit(): void {
    this.tryAttachPivotListener();
  }

  ngOnDestroy(): void {
    if (this.pivotListener && this.grid?.gridApi) {
      this.grid.gridApi.removeEventListener('columnPivotModeChanged', this.pivotListener);
    }
  }

  public onGridFirstDataRendered(): void {
    this.tryAttachPivotListener();
    const currentColumnApi = this.grid?.gridColumnApi;
    if (currentColumnApi && currentColumnApi !== this.lastCapturedGridApi) {
      this.lastCapturedGridApi = currentColumnApi;
      this.initialColumnState = currentColumnApi.getColumnState();
    }
    this.applyPivotDynamicVisibility();
  }

  private tryAttachPivotListener(): void {
    if (this.grid?.gridApi && this.grid?.gridColumnApi) {
      if (this.pivotListener) {
        this.grid.gridApi.removeEventListener('columnPivotModeChanged', this.pivotListener);
      }
      this.pivotListener = () => {
        this.pivotEnabled = !!this.grid.gridColumnApi.isPivotMode();
        if (!this.pivotEnabled) {
          this.cleanupGroupingAndPinning();
        } else {
          this.restoreDefaultGroupingAndPinning();
        }
        this.applyPivotDynamicVisibility();
        this.cdr.detectChanges();
      };
      this.grid.gridApi.addEventListener('columnPivotModeChanged', this.pivotListener);
      this.pivotEnabled = !!this.grid.gridColumnApi.isPivotMode();
      this.cdr.detectChanges();
    }
  }

  private cleanupGroupingAndPinning(): void {
    if (!this.grid?.gridApi) return;
    this.grid.gridColumnApi.setRowGroupColumns([]);
    const allColumns = this.grid.gridColumnApi.getColumns() ?? [];
    if (allColumns.length > 0) {
      this.grid.gridColumnApi.applyColumnState({
        state: allColumns.map((col: any) => ({ colId: col.getColId(), pinned: null, sort: null, sortIndex: null }))
      });
    }
  }

  private restoreDefaultGroupingAndPinning(): void {
    if (!this.grid?.gridApi || !this.initialColumnState) return;
    const stateToApply = this.initialColumnState.map(col => ({
      colId: col.colId,
      rowGroup: col.rowGroup ?? false,
      pinned: col.pinned ?? null
    }));
    this.grid.gridColumnApi.applyColumnState({ state: stateToApply });
  }

  private applyPivotDynamicVisibility(): void {
    if (!this.grid?.gridColumnApi) return;
    const state = this.pivotDynamicColumns.map(colId => ({
      colId,
      hide: this.pivotEnabled
    }));
    this.grid.gridColumnApi.applyColumnState({ state });
  }
}
